import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase, keyframes, Typography } from '@mui/material';
import { renderVysorBotContent } from '../../utils/vysorChatRender';
import {
  LAB,
  labHairline,
  labInk,
  labMuted,
  labSurface,
  labUserBubble,
} from './vysorLabTheme';

export type LabQuickOption = {
  id: string;
  label: string;
  description?: string;
};

export type LabChatMessage = {
  id: string;
  role: 'user' | 'bot';
  content: string;
  options?: LabQuickOption[];
};

type Props = {
  messages: LabChatMessage[];
  loading: boolean;
  activeOptionsMessageId: string | null;
  mode: 'light' | 'dark';
  onOption: (option: LabQuickOption) => void;
};

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.28; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`;

function VysorMark({ size = 28, mode }: { size?: number; mode: 'light' | 'dark' }) {
  const ink = labInk(mode);
  const surface = labSurface(mode);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '9px',
        bgcolor: ink,
        color: surface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontFamily: LAB.fontBody,
        fontWeight: 700,
        fontSize: size * 0.38,
        letterSpacing: '-0.04em',
      }}
    >
      V
    </Box>
  );
}

const VysorMessageList: React.FC<Props> = ({
  messages,
  loading,
  activeOptionsMessageId,
  mode,
  onOption,
}) => {
  const endRef = useRef<HTMLDivElement>(null);
  const ink = labInk(mode);
  const muted = labMuted(mode);
  const hairline = labHairline(mode);
  const userBg = labUserBubble(mode);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, md: 3 },
        pb: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: LAB.column,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {messages.map((m) => {
          const isUser = m.role === 'user';
          const showOptions =
            !isUser && m.id === activeOptionsMessageId && (m.options?.length || 0) > 0;

          return (
            <Box
              key={m.id}
              sx={{
                animation: 'labMsgIn 360ms cubic-bezier(0.22, 1, 0.36, 1)',
                '@keyframes labMsgIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'none' },
                },
              }}
            >
              {isUser ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box
                    sx={{
                      maxWidth: '78%',
                      px: 2.1,
                      py: 1.4,
                      borderRadius: `${LAB.radius.message}px`,
                      bgcolor: userBg,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: LAB.fontBody,
                        fontSize: '0.98rem',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.55,
                        color: ink,
                        fontWeight: 450,
                      }}
                    >
                      {m.content}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <VysorMark mode={mode} size={28} />
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
                    <Box
                      sx={{
                        fontFamily: LAB.fontBody,
                        fontSize: '1.02rem',
                        lineHeight: 1.72,
                        color: ink,
                        '& strong': { fontWeight: 650 },
                        '& a': { color: ink, textUnderlineOffset: 3 },
                        '& p': { my: 0.6 },
                      }}
                    >
                      {renderVysorBotContent(m.content, '1.02rem')}
                    </Box>

                    {showOptions && (
                      <Box
                        sx={{
                          mt: 2,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.85,
                        }}
                      >
                        {m.options!.map((opt) => (
                          <ButtonBase
                            key={opt.id}
                            onClick={() => onOption(opt)}
                            disabled={loading}
                            sx={{
                              px: 1.6,
                              py: 0.9,
                              borderRadius: `${LAB.radius.chip}px`,
                              border: `1px solid ${hairline}`,
                              bgcolor: 'transparent',
                              fontFamily: LAB.fontBody,
                              fontSize: '0.88rem',
                              fontWeight: 500,
                              color: ink,
                              transition: 'background-color 140ms, border-color 140ms',
                              '&:hover': {
                                bgcolor:
                                  mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                borderColor:
                                  mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
                              },
                              '&.Mui-disabled': { opacity: 0.45 },
                            }}
                          >
                            {opt.label}
                          </ButtonBase>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <VysorMark mode={mode} size={28} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, py: 0.5 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: muted,
                    animation: `${pulse} 1.05s ease-in-out ${i * 0.15}s infinite`,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <div ref={endRef} />
      </Box>
    </Box>
  );
};

export default VysorMessageList;
